import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import {
  electricityMarkerFromLive,
  getFernausleseOverview,
  type PitchElectricityMarker,
} from "@/lib/fernausleseApi";
import {
  computeSitePlanStatusLocal,
  type SitePlanSettings,
  type SitePlanStatusResponse,
} from "@/lib/sitePlan";
import type { Booking, Pitch, PitchCategory } from "@/types/camping";

const REFRESH_DEBOUNCE_MS = 400;
const ELECTRICITY_REFRESH_MS = 60_000;

type LoadOptions = { silent?: boolean };

export function useSitePlanPageData(referenceDate: string) {
  const [planImage, setPlanImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState<string | null>(null);
  const [sitePlanData, setSitePlanData] = useState<SitePlanStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusFromFallback, setStatusFromFallback] = useState(false);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [pitchBookings, setPitchBookings] = useState<Booking[]>([]);
  const [pitchBookingsLoading, setPitchBookingsLoading] = useState(false);
  const [electricityByPitch, setElectricityByPitch] = useState<
    Record<string, PitchElectricityMarker>
  >({});

  const pitchesRef = useRef(pitches);
  pitchesRef.current = pitches;

  const loadPlanImage = useCallback((options?: LoadOptions) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      setImageLoading(true);
      setImageError(null);
    }
    api
      .get<SitePlanSettings>("/api/v1/settings/site-plan?include_image=true")
      .then((settings) => setPlanImage(settings.image_data ?? null))
      .catch((e: Error) => {
        setPlanImage(null);
        setImageError(e.message);
      })
      .finally(() => setImageLoading(false));
  }, []);

  const loadStaticData = useCallback(async () => {
    try {
      const [pitchesList, categoriesList] = await Promise.all([
        api.get<Pitch[]>("/api/v1/pitches"),
        api.get<PitchCategory[]>("/api/v1/pitch-categories").catch(() => [] as PitchCategory[]),
      ]);
      setPitches(pitchesList);
      setCategories(categoriesList);
    } catch {
      /* Stichtags-Status zeigt Fehler separat */
    }
  }, []);

  const loadStatus = useCallback(
    async (options?: LoadOptions) => {
      const silent = options?.silent ?? false;
      if (!silent) {
        setStatusLoading(true);
        setStatusError(null);
        setStatusFromFallback(false);
      }

      const params = new URLSearchParams({ reference_date: referenceDate });

      try {
        let statusData: SitePlanStatusResponse;
        try {
          statusData = await api.get<SitePlanStatusResponse>(
            `/api/v1/site-plan/status?${params}`,
          );
        } catch (statusErr) {
          const canFallback =
            statusErr instanceof ApiError &&
            (statusErr.status === 404 || statusErr.status === 0 || statusErr.status >= 500);
          if (!canFallback) throw statusErr;

          let pitchesList = pitchesRef.current;
          if (pitchesList.length === 0) {
            pitchesList = await api.get<Pitch[]>("/api/v1/pitches");
            setPitches(pitchesList);
          }

          const [bookingsList, planMeta] = await Promise.all([
            api.get<Booking[]>("/api/v1/bookings"),
            api
              .get<SitePlanSettings>("/api/v1/settings/site-plan?include_image=false")
              .catch(() => null),
          ]);

          statusData = computeSitePlanStatusLocal(pitchesList, bookingsList, referenceDate, {
            wetBlockingActive: planMeta?.wet_pitch_blocking_active,
            badgeScale: planMeta?.badge_scale,
          });
          setStatusFromFallback(true);
        }
        setSitePlanData(statusData);
      } catch (e) {
        if (!silent) {
          setSitePlanData(null);
          setStatusError(e instanceof Error ? e.message : "Platzplan konnte nicht geladen werden.");
        }
      } finally {
        if (!silent) setStatusLoading(false);
      }
    },
    [referenceDate],
  );

  const loadPitchBookings = useCallback(
    async (pitchId: string, rangeFrom: string, rangeToExclusive: string) => {
      setPitchBookingsLoading(true);
      try {
        const params = new URLSearchParams({
          from: rangeFrom,
          to: rangeToExclusive,
          pitch_id: pitchId,
        });
        const bookings = await api.get<Booking[]>(
          `/api/v1/site-plan/pitch-bookings?${params}`,
        );
        setPitchBookings(bookings);
      } catch {
        setPitchBookings([]);
      } finally {
        setPitchBookingsLoading(false);
      }
    },
    [],
  );

  const electricityInFlight = useRef(false);

  const loadElectricity = useCallback((silent = false) => {
    if (silent && electricityInFlight.current) return;
    electricityInFlight.current = true;
    getFernausleseOverview(true)
      .then((overview) => {
        const next: Record<string, PitchElectricityMarker> = {};
        for (const row of overview.rows) {
          const marker = electricityMarkerFromLive(row.live, row.has_electricity);
          if (marker) next[row.pitch_id] = marker;
        }
        setElectricityByPitch(next);
      })
      .catch(() => {
        if (!silent) setElectricityByPitch({});
      })
      .finally(() => {
        electricityInFlight.current = false;
      });
  }, []);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    if (refreshDebounceRef.current != null) {
      clearTimeout(refreshDebounceRef.current);
    }
    refreshDebounceRef.current = setTimeout(() => {
      refreshDebounceRef.current = null;
      void loadStatus({ silent: true });
      loadElectricity(true);
    }, REFRESH_DEBOUNCE_MS);
  }, [loadElectricity, loadStatus]);

  useEffect(() => {
    return () => {
      if (refreshDebounceRef.current != null) {
        clearTimeout(refreshDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    void loadStaticData();
    loadPlanImage();
  }, [loadStaticData, loadPlanImage]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadElectricity();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") loadElectricity(true);
    }, ELECTRICITY_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadElectricity]);

  return {
    planImage,
    imageLoading,
    imageError,
    sitePlanData,
    statusLoading,
    statusError,
    statusFromFallback,
    pitches,
    categories,
    pitchBookings,
    pitchBookingsLoading,
    loadPitchBookings,
    electricityByPitch,
    refresh,
  };
}
